import { formatDate, parseISO } from "date-fns"
import { Badge } from "design-system/atoms/Badge"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "design-system/atoms/Collapsible"
import { Separator } from "design-system/atoms/Separator"
import { ChevronRight } from "lucide-react"
import { useIsAmphoraCompany } from "modules/customers/hooks/useIsAmphoraCompany"
import { useTranslation } from "react-i18next"
import { CustomerIssueResponseDataSchema } from "schema"
import { z } from "zod"
import { AmphoraActivityPanel } from "../SupportTimeline/AmphoraActivityPanel"
import { GorgiasActivityPanel } from "../SupportTimeline/GorgiasActivityPanel"
import { ZendeskAuditPanel } from "../SupportTimeline/ZendeskAuditPanel"
import { TicketIntegrationBadges } from "../TicketIntegrationBadges"
import { IssueMedia } from "./IssueMedia"
import { IssueCloseButton } from "./IssueCloseButton"
import { IssueUpdatesTimeline } from "./IssueUpdatesTimeline"
import { IssueStatusBadge } from "./IssueUrgencyIdentifier"

type Props = {
  issue: z.infer<typeof CustomerIssueResponseDataSchema>
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  onConversationClick: (conversationId: string) => void
}

export function IssueCard({ issue, isOpen, onOpenChange, onConversationClick }: Props) {
  const { t } = useTranslation()

  const isAmphoraCompany = useIsAmphoraCompany()
  const hasActivity =
    isAmphoraCompany ||
    issue.integrations.some(
      (integration) =>
        integration.kind === "Zendesk" ||
        integration.kind === "Amphora" ||
        integration.kind === "Gorgias",
    )

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={onOpenChange}
      className="w-full overflow-hidden rounded-lg border border-border"
    >
      <CollapsibleTrigger asChild>
        <button type="button" className="group w-full text-left cursor-pointer">
          <div className="flex items-center justify-between gap-4 bg-muted px-4 py-3">
            <div className="flex min-w-0 items-center gap-3">
              <IssueStatusBadge issue={issue} className="bg-muted" />
              <span className="min-w-0 text-base font-medium leading-snug text-foreground">
                {issue.subject}
              </span>
            </div>
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-4">
              <div className="flex flex-row gap-2">
                <TicketIntegrationBadges
                  integrations={issue.integrations}
                  tooltipKey="support.entities.escalatedThrough"
                  className="bg-muted"
                />
                {issue.category && (
                  <Badge
                    variant="secondary"
                    className="text-muted-foreground bg-muted-foreground/10"
                  >
                    {issue.category}
                  </Badge>
                )}
              </div>
              <span className="shrink-0 text-sm text-muted-foreground">
                {formatDate(parseISO(issue.updatedAt), "P")}
              </span>
              <ChevronRight className="size-3.5 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-90" />
            </div>
          </div>
          <div className="px-4 pt-3">
            <p className="whitespace-pre-wrap text-sm leading-snug text-foreground">
              {issue.summary}
            </p>
          </div>
        </button>
      </CollapsibleTrigger>
      {/* Outside the trigger so the resolve button isn't nested inside a button */}
      <div className="flex items-center justify-between gap-4 pl-4 pr-2 py-1.5">
        <span className="text-xs text-muted-foreground">
          {t("support.entities.issueOccurrenceCount", {
            count: new Set(issue.updates.flatMap((update) => update.conversationId ?? [])).size,
          })}
        </span>
        <IssueCloseButton issue={issue} />
      </div>
      <CollapsibleContent>
        <div className="flex flex-col gap-4 px-4 pb-4">
          <Separator />
          <IssueUpdatesTimeline issue={issue} onConversationClick={onConversationClick} />
          <IssueMedia media={issue.media} />
          {hasActivity && (
            <div className="flex flex-col gap-4">
              <Separator />
              <ZendeskAuditPanel issueId={issue.id} integrations={issue.integrations} />
              <AmphoraActivityPanel issueId={issue.id} integrations={issue.integrations} />
              <GorgiasActivityPanel issueId={issue.id} integrations={issue.integrations} />
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
